import React, { useState, useEffect } from 'react';
import { Shield, Database, Users, BookOpen, Zap, CheckCircle, Cpu, Globe, Lock } from 'lucide-react';

export default function Loading({ user }) {
  const [isLoading, setIsLoading] = useState(true);
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [particleAnimation, setParticleAnimation] = useState(0);
  const [systemChecks, setSystemChecks] = useState([]);
  const [darkMode] = useState(true);

  const loadingStages = [
    { 
      id: 0, 
      title: 'Initializing Core Systems', 
      subtitle: 'Establishing secure connections',
      icon: Shield,
      duration: 1500,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      id: 1, 
      title: 'Loading Database', 
      subtitle: 'Syncing user data and preferences',
      icon: Database,
      duration: 2000,
      color: 'from-purple-500 to-pink-500'
    },
    { 
      id: 2, 
      title: 'Authenticating Session', 
      subtitle: 'Verifying security protocols',
      icon: Lock,
      duration: 1200,
      color: 'from-orange-500 to-red-500'
    },
    { 
      id: 3, 
      title: 'Preparing Interface', 
      subtitle: 'Optimizing for your experience',
      icon: Cpu,
      duration: 1800,
      color: 'from-green-500 to-emerald-500'
    },
    { 
      id: 4, 
      title: 'Finalizing Setup', 
      subtitle: 'Almost ready...',
      icon: CheckCircle,
      duration: 1000,
      color: 'from-violet-500 to-purple-500'
    }
  ];

  

  useEffect(() => {
    let totalDuration = 0;
    let currentProgress = 0;

    const runStages = async () => {
      for (let i = 0; i < loadingStages.length; i++) {
        setCurrentStage(i);
        
        // Animate progress for current stage
        const stageStart = currentProgress;
        const stageEnd = ((i + 1) / loadingStages.length) * 100;
        const stageDuration = loadingStages[i].duration;
        
        const progressInterval = setInterval(() => {
          currentProgress += (stageEnd - stageStart) / (stageDuration / 50);
          if (currentProgress >= stageEnd) {
            currentProgress = stageEnd;
            clearInterval(progressInterval);
          }
          setProgress(currentProgress);
        }, 50);

        await new Promise(resolve => setTimeout(resolve, stageDuration));
        clearInterval(progressInterval);
      }

      // Final delay before showing content
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };

    runStages();

    // Particle animation
    const particleInterval = setInterval(() => {
      setParticleAnimation(prev => prev + 1);
    }, 100);

    // System checks animation
    systemChecks.forEach((check, index) => {
      setTimeout(() => {
        setSystemChecks(prev => {
          const newChecks = [...prev];
          newChecks[index] = { ...check, status: 'loading' };
          return newChecks;
        });
        
        setTimeout(() => {
          setSystemChecks(prev => {
            const newChecks = [...prev];
            newChecks[index] = { ...check, status: 'complete' };
            return newChecks;
          });
        }, 800);
      }, check.delay);
    });

    return () => {
      clearInterval(particleInterval);
    };
  }, []);

  if (!isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl font-bold text-white mb-4">Welcome to Peacecraft!</div>
          <div className="text-slate-400">Loading complete. Ready to start learning.</div>
        </div>
      </div>
    );
  }

  const currentStageData = loadingStages[currentStage];
  const CurrentIcon = currentStageData?.icon || Shield;

  return (
    <div className={`fixed inset-0 transition-all duration-1000 overflow-hidden ${
      darkMode ? 'bg-slate-900' : 'bg-blue-50'
    }`}>
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400 rounded-full opacity-30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
            animate-float
          />
        ))}
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-8">
        <div className="max-w-2xl w-full">
          {/* Main Brand */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center transform rotate-12 hover:rotate-0 transition-transform duration-500">
                <Shield className="w-10 h-10 text-white" />
              </div>
            </div>
            <div className={`text-6xl font-light mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent`}>
              PEACECRAFT
            </div>
            <div className={`text-xl mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-500'}`}>
              Learning Platform
            </div>
           <div className={`text-lg ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {user ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: `Welcome back, <span class="font-semibold text-white">${user.username}</span>`,
                  }}
                />
              ) : (
                'Loading your personalized experience...'
              )}
            </div>
          </div>

          {/* Current Stage Display */}
          <div className="text-center mb-12">
            <div className="relative inline-block mb-6">
              <div className={`w-24 h-24 bg-gradient-to-r ${currentStageData.color} rounded-full flex items-center justify-center animate-pulse`}>
                <CurrentIcon className="w-12 h-12 text-white" />
              </div>
              <div className="absolute inset-0 rounded-full border-4 border-white/20 animate-spin-slow"></div>
              <div className="absolute -inset-2 rounded-full border border-white/10 animate-ping"></div>
            </div>
            
            <div className="space-y-2">
              <div className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                {currentStageData.title}
              </div>
              <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {currentStageData.subtitle}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between mb-2">
              <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Loading Progress
              </span>
              <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {Math.round(progress)}%
              </span>
            </div>
            <div className={`h-3 bg-gradient-to-r ${darkMode ? 'from-slate-800 to-slate-700' : 'from-blue-100 to-purple-100'} rounded-full overflow-hidden shadow-inner`}>
              <div 
                className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-300 ease-out relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 animate-shimmer"></div>
              </div>
            </div>
          </div>

          {/* Stage Indicators */}
          <div className="flex justify-between mb-12">
            {loadingStages.map((stage, index) => {
              const StageIcon = stage.icon;
              return (
                <div key={stage.id} className="flex flex-col items-center space-y-2">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                    index <= currentStage 
                      ? `bg-gradient-to-r ${stage.color} border-transparent text-white scale-110` 
                      : `${darkMode ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-400'}`
                  }`}>
                    {index < currentStage ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <StageIcon className={`w-6 h-6 ${index === currentStage ? 'animate-pulse' : ''}`} />
                    )}
                  </div>
                  <div className={`text-xs text-center max-w-20 ${
                    index <= currentStage 
                      ? darkMode ? 'text-white' : 'text-slate-800'
                      : darkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}>
                    {stage.title.split(' ')[0]}
                  </div>
                </div>
              );
            })}
          </div>

          {/* System Status */}
          <div className={`bg-gradient-to-r ${darkMode ? 'from-slate-800/50 to-slate-700/50' : 'from-white/50 to-slate-50/50'} backdrop-blur-md rounded-2xl p-6 border ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
            <div className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              System Status
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {systemChecks.map((check, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {check.name}
                  </span>
                  <div className="flex items-center">
                    {systemChecks[index]?.status === 'complete' && (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    )}
                    {systemChecks[index]?.status === 'loading' && (
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                    {(!systemChecks[index] || systemChecks[index]?.status === 'pending') && (
                      <div className={`w-4 h-4 rounded-full ${darkMode ? 'bg-slate-600' : 'bg-slate-300'}`}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Loading Tips */}
          <div className="text-center mt-8">
            <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'} italic`}>
              "Great things take time. We're crafting your perfect learning experience."
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(5px) rotate(-1deg); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(12deg); }
          100% { transform: translateX(200%) skewX(12deg); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}